import { useEffect, useState } from "react";

/*
 * Homepage scroll feel: momentum, damped ends, and auto-hiding chrome.
 *
 * WHY THIS DRIVES NATIVE scrollTop.
 * The usual way to build momentum scrolling is to lock the body and translate a wrapper element.
 * That is what Locomotive and friends do, and it breaks a long list of things quietly: `position:
 * sticky` (this page's nav), anchor links, find-in-page, the real scrollbar, focus scrolling, and
 * every assistive technology that asks the document where it is. This module instead accumulates a
 * TARGET scroll position and eases the REAL one toward it, so the page is still a normally
 * scrolled document that happens to arrive smoothly. Everything above keeps working untouched.
 *
 * WHERE IT REFUSES TO RUN. Hijacking the wheel is a real imposition, so it is declined whenever it
 * would be unwelcome or actively worse:
 *   · `prefers-reduced-motion: reduce` — smoothed scrolling is a vestibular trigger. Not negotiable.
 *   · coarse pointers — a touchscreen already has momentum, tuned by the OS to the device. Taking
 *     that over is strictly worse than leaving it alone.
 *   · ctrl/cmd + wheel — that is zoom, and it must reach the browser.
 * In every one of those cases the page scrolls natively and nothing here is installed at all.
 *
 * The tuning constants below are deliberately exported. This is a feel, and a feel needs a knob —
 * numbers that read fine here are judged on a real trackpad and a real mouse wheel, which behave
 * nothing like each other.
 */

// How much of the remaining distance is covered each frame. Higher is snappier, lower is floatier.
// 0.115 lands at roughly a 250ms settle at 60fps, which reads as momentum rather than as lag.
export const EASE = 0.115;

// How close to either end of the page the input starts meeting resistance.
export const EDGE_ZONE = 240;

// A single wheel event's biggest allowed contribution. Some mice emit enormous one-shot deltas,
// and without this a single flick throws the target most of the way down the document.
export const MAX_STEP = 220;

// The floor that keeps the damped ends REACHABLE. See edgeResistance.
export const MIN_STEP = 10;

/* How close to a section boundary a rest position has to be before the page finishes the job, as
   a fraction of the viewport.

   This is the number that decides whether snapping is a help or an imposition, so it is small on
   purpose. At 14% of a 800px viewport it only acts when someone has already stopped within about
   110px of a boundary — a stop that was going to be a section anyway. Anyone who deliberately
   halts mid-section to read is left exactly where they put themselves, which is the difference
   between this and the mandatory full-page snapping that makes a site feel like it is arguing
   with the scroll wheel. */
export const SNAP_ZONE = 0.14;

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

/**
 * How much of an incoming wheel delta survives near the ends of the page.
 *
 * The brief: in the hero, still scrolling up — and at the footer, still scrolling down — the page
 * should slow and eventually stop rather than hit a wall. So input pushing TOWARD the end it is
 * already near gets scaled down by the square of how much room is left; input pushing away is
 * never touched, so escaping is always instant.
 *
 * The `MIN_STEP` floor is what stops this from being a trap. A pure quadratic falloff approaches
 * the boundary asymptotically, which would mean the last few pixels of the document — the footer —
 * could never actually be reached. The floor guarantees every tick still closes at least 10px, or
 * exactly the remaining distance when less than that, so the end is always attainable and is
 * landed on precisely. Once there, `distance` is 0 and the result is 0: pushing further does
 * nothing, which is the "eventually becomes zero" the feel is named for.
 *
 * @param {number} target  current target scroll position
 * @param {number} delta   requested change, negative scrolls toward the top
 * @param {number} max     maximum scrollable offset
 * @param {number} zone    how far from an end the damping starts
 * @returns {number} the damped delta
 */
export function edgeResistance(target, delta, max, zone = EDGE_ZONE) {
  if (!delta || max <= 0) return 0;

  // Room left in the direction of travel. Negative delta heads for the top, so the room is
  // whatever is above; positive heads for the bottom.
  const distance = delta < 0 ? target : max - target;
  if (distance <= 0) return 0;          // already pressed against this end
  if (distance >= zone) return delta;   // out in open water, no damping at all

  const t = distance / zone;
  const damped = delta * t * t;

  // Never strand the user short of the end.
  const floor = Math.min(MIN_STEP, distance);
  return Math.abs(damped) < floor ? Math.sign(delta) * floor : damped;
}

/**
 * One frame of easing toward the target.
 *
 * This is also where "scroll slowly and the page moves slowly, scroll fast and it moves fast"
 * comes from, without measuring speed anywhere: fast input stacks up more distance between current
 * and target, and a constant fraction of a bigger gap is a bigger step. Velocity response falls
 * out of the lerp for free.
 *
 * Snapping inside half a pixel matters — without it the loop runs forever chasing a remainder it
 * can never close, burning a frame callback for the life of the page.
 */
export function stepToward(current, target, ease = EASE) {
  const next = current + (target - current) * ease;
  return Math.abs(target - next) < 0.5 ? target : next;
}

/** A wheel event's delta in pixels, whatever unit the device reports it in. */
export function wheelDelta(event, viewportHeight) {
  const { deltaY, deltaMode } = event;
  // 0 = pixels, 1 = lines, 2 = pages.
  const scale = deltaMode === 1 ? 16 : deltaMode === 2 ? viewportHeight : 1;
  return clamp(deltaY * scale, -MAX_STEP, MAX_STEP);
}

/**
 * Whether a scroll event came from somewhere other than this engine, and should therefore be
 * adopted rather than overridden.
 *
 * Syncing only while idle is not enough, and the gap is reachable: flick the wheel and then click
 * a nav anchor before the glide settles. The anchor moves the page, the still-running loop has an
 * older target, and it drags the reader straight back — the link looks broken. Comparing against
 * the position this engine last wrote catches that, because anything else that moves the document
 * lands somewhere the loop did not put it.
 *
 * The tolerance absorbs sub-pixel and rounding differences between what was written and what the
 * document reports back; anything larger is genuinely someone else.
 */
export function adoptsExternalScroll(y, lastWritten, idle, tolerance = 2) {
  return idle || Math.abs(y - lastWritten) > tolerance;
}

/**
 * The nearest section boundary worth finishing on, or null to stay put.
 *
 * Deliberately returns null rather than the closest point when nothing is close: "no opinion" and
 * "the nearest one is miles away" have to be different answers, or the page drags the reader to a
 * heading every single time they stop.
 */
export function nearestSnap(y, points, threshold) {
  if (!points || !points.length || threshold <= 0) return null;
  let best = null;
  let bestDistance = Infinity;
  for (const point of points) {
    const distance = Math.abs(point - y);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = point;
    }
  }
  return bestDistance <= threshold ? best : null;
}

/** Whether smoothing should be installed at all. See the header for why each case opts out. */
export function smoothingWanted(win = typeof window !== "undefined" ? window : undefined) {
  if (!win || typeof win.matchMedia !== "function") return false;
  if (win.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  if (win.matchMedia("(pointer: coarse)").matches) return false;
  return true;
}

/**
 * Install the momentum scroll. Returns a teardown function.
 *
 * Kept separate from the React hook so it can be reasoned about — and driven — without a renderer.
 */
export function installSmoothScroll(win = window) {
  const doc = win.document.documentElement;

  /* The page's scrollable height is CACHED rather than read per event, and that is a performance
     decision, not laziness. `scrollHeight` is a forced synchronous layout whenever style or layout
     is dirty, and a wheel handler runs upwards of a hundred times a second — this page already has
     a rule about exactly that (see test_layout_reads_are_confined_to_the_scroll_reveal, written
     after a tilt effect measured a reflow per pointer event).

     Refreshed on resize and once per gesture, which is every occasion it can actually change here:
     the reveal animates opacity and transform, so it never alters the document's height, and the
     hero film reserves its box before it loads. Anything that DOES start changing page height
     mid-scroll needs to call `remeasure` — hence it being named rather than inlined. */
  let max = 0;
  let snapPoints = [];
  const remeasure = () => {
    max = Math.max(0, doc.scrollHeight - win.innerHeight);
    // Section tops in document space. Measured here with the height, on the same schedule, for the
    // same reason — this is layout, and layout is not something to read per event.
    snapPoints = Array.from(win.document.querySelectorAll(".lp section"))
      .map((el) => Math.round(el.getBoundingClientRect().top + win.scrollY))
      .filter((y) => y > 0 && y < max);
  };
  remeasure();

  let target = win.scrollY;
  let current = target;
  let frame = 0;
  // The last offset this engine wrote. `onScroll` compares against it to tell its own scrolling
  // apart from everyone else's — see adoptsExternalScroll.
  let lastWritten = target;
  // While idle, the page belongs to everyone else: anchor clicks, the keyboard, a scrollbar drag,
  // find-in-page. Tracking that is what keeps this from fighting them.
  let idle = true;
  // Set by wheel input only, cleared the moment it is acted on: snapping is a courtesy extended to
  // a gesture that has finished, never something applied to a position somebody else chose.
  let wantSnap = false;

  const run = () => {
    current = stepToward(current, target);
    lastWritten = current;
    win.scrollTo(0, current);

    if (current === target) {
      /* Coming to rest is the only moment it is safe to tidy the position: mid-glide the reader is
         still going somewhere, and moving the destination under them is how snapping earns its bad
         name. Only a wheel gesture arms this, so an anchor jump or a keyboard scroll lands exactly
         where it was aimed.

         The two ends are excluded outright. The top and the bottom of the document are already
         meaningful places to be, and pulling someone off the footer to align a heading they have
         already read past would be actively wrong. */
      if (wantSnap) {
        wantSnap = false;
        if (target > 2 && target < max - 2) {
          const landing = nearestSnap(target, snapPoints, win.innerHeight * SNAP_ZONE);
          if (landing !== null && Math.abs(landing - target) > 1) {
            target = clamp(landing, 0, max);
            frame = win.requestAnimationFrame(run);
            return;
          }
        }
      }
      frame = 0;
      idle = true;
      return;
    }
    frame = win.requestAnimationFrame(run);
  };

  const onWheel = (event) => {
    // Zoom, and anything another handler has already claimed, must pass straight through.
    if (event.ctrlKey || event.metaKey || event.defaultPrevented) return;

    const delta = wheelDelta(event, win.innerHeight);
    if (!delta) return;

    // A new gesture is the natural moment to re-measure: once per flick, not once per event.
    if (idle) remeasure();
    if (max <= 0) return;

    event.preventDefault();

    // Pick up wherever the page actually is, not wherever this module last left it.
    if (idle) {
      current = win.scrollY;
      target = current;
      // Recording it as "known" is what stops a queued scroll event from undoing this gesture.
      // `scrollTo` dispatches its scroll event asynchronously, so an external jump immediately
      // followed by wheel input — an anchor click and a flick in the same breath — delivers that
      // event AFTER these handlers have run. With a stale `lastWritten` it looks like somebody
      // else moved the page, and the adopt branch throws the gesture away. Observed doing exactly
      // that: a 40-tick burst that moved nothing at all.
      lastWritten = current;
      idle = false;
    }

    target = clamp(target + edgeResistance(target, delta, max), 0, max);
    wantSnap = true;

    /* Pressed against the cached bottom is precisely when the cache is most likely to be stale, so
       spend one measurement to check. Observed in the browser: a long unbroken flick into the
       footer stopped 110px short, because the document grew mid-gesture as the reveal animations
       settled and the clamp was still using the height from when the flick started.
       This costs a layout read only at the boundary — never on an ordinary event. */
    if (target >= max) {
      remeasure();
      target = clamp(target, 0, max);
    }

    if (!frame) frame = win.requestAnimationFrame(run);
  };

  const onScroll = () => {
    const y = win.scrollY;
    if (!adoptsExternalScroll(y, lastWritten, idle)) return;
    // Someone else moved the page — an anchor, the keyboard, a scrollbar drag, find-in-page.
    // Adopt their position and stand down rather than dragging the reader back.
    current = y;
    target = y;
    lastWritten = y;
    // Disarm the snap along with it. A gesture in progress arms snapping; if an anchor lands while
    // that is still pending, the loop settles on the anchor's exact destination and then tidies it
    // away to the nearest heading — a link that visibly overshoots where it was pointed.
    wantSnap = false;
  };

  win.addEventListener("wheel", onWheel, { passive: false });
  win.addEventListener("scroll", onScroll, { passive: true });
  win.addEventListener("resize", remeasure, { passive: true });

  return () => {
    win.removeEventListener("wheel", onWheel);
    win.removeEventListener("scroll", onScroll);
    win.removeEventListener("resize", remeasure);
    if (frame) win.cancelAnimationFrame(frame);
  };
}

/*
 * The auto-hiding nav.
 *
 * Reads `window.scrollY` only — never element geometry — so it costs nothing per frame and works
 * the same whether the smoothing above is installed or the page is scrolling natively.
 */

// Movement below this is jitter, or a trackpad resting under a thumb. Reacting to it makes the bar
// flicker on and off around a stationary reading position.
export const NAV_THRESHOLD = 8;

// Above the fold the bar is always shown, regardless of direction. Hiding chrome while someone is
// still looking at the headline reads as a glitch, not as a feature.
export const NAV_ALWAYS_VISIBLE_ABOVE = 90;

/**
 * Whether the nav should be hidden, given where the page is and which way it moved.
 * Pure, so the direction rule can be tested without a scroller.
 */
export function navHiddenNext(y, dy, wasHidden) {
  if (y < NAV_ALWAYS_VISIBLE_ABOVE) return false;
  if (Math.abs(dy) < NAV_THRESHOLD) return wasHidden;
  return dy > 0; // moving toward later sections hides it; back toward earlier ones brings it down
}

/* ── React bindings ───────────────────────────────────────────────────────── */

/**
 * Momentum scrolling for as long as the component is mounted, where it is wanted.
 *
 * The reduced-motion query is re-read on change rather than only at mount: someone turning the OS
 * setting on mid-session is asking for the motion to stop now, not after a reload.
 */
export function useSmoothScroll() {
  useEffect(() => {
    let teardown = null;
    const apply = () => {
      if (smoothingWanted()) {
        if (!teardown) teardown = installSmoothScroll(window);
      } else if (teardown) {
        teardown();
        teardown = null;
      }
    };
    apply();

    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    // Safari below 14 only has the deprecated listener API.
    const listen = query.addEventListener ? "addEventListener" : "addListener";
    const stop = query.removeEventListener ? "removeEventListener" : "removeListener";
    query[listen]("change", apply);

    return () => {
      query[stop]("change", apply);
      if (teardown) teardown();
    };
  }, []);
}

/**
 * True while the nav should be slid out of the way.
 *
 * Returns false forever under reduced motion: chrome that comes and goes as the page moves IS
 * motion, and the honest response to that preference is a bar that simply stays put.
 */
export function useNavAutoHide() {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;

    let last = window.scrollY;
    let frame = 0;

    const read = () => {
      frame = 0;
      const y = window.scrollY;
      const dy = y - last;
      // `last` only advances once the movement is worth reacting to, so a slow drag accumulates
      // instead of being discarded a pixel at a time and never reaching the threshold.
      if (Math.abs(dy) >= NAV_THRESHOLD) last = y;
      setHidden((was) => navHiddenNext(y, dy, was));
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return hidden;
}
